<template>
  <div class="mb-4 mt-6 rounded-md bg-white p-4">
    <div class="mb-4 grid grid-cols-1 items-start gap-4 border-b pb-4 md:grid-cols-3">
      <InputField name="name" :label="t('name')" />
      <SelectField name="type" :label="t('documentType')" :options="documentTypes" />
      <FileInputField name="file" :label="t('upload')" />
    </div>
    <div class="flex justify-end gap-2">
      <CVButton variant="default" :is-loading="isUploadingDocument" @click.prevent="saveDocument">
        {{ t('saveDocument') }}
      </CVButton>
      <CVButton :disabled="isUploadingDocument" @click.prevent="$emit('complete')">
        {{ t('cancel') }}
      </CVButton>
    </div>
  </div>
</template>

<script lang="ts" setup>
import FileInputField from '@/components/FileInputField.vue';
import InputField from '@/components/InputField.vue';
import SelectField from '@/components/SelectField.vue';
import CVButton from '@/components/buttons/CVButton.vue';
import { useI18n } from 'vue-i18n';
import { toTypedSchema } from '@vee-validate/yup';
import { useForm } from 'vee-validate';
import * as yup from 'yup';
import { computed } from 'vue';
import { useUploadAndCreateWorkshopAppointmentDocument } from '@/hooks/use-upload-and-create-workshop-appointment-document';
import { WorkshopAppointmentDocumentType } from '@carvia/ros-client-types';

const props = defineProps<{
  workshopAppointmentId: string;
}>();

const emit = defineEmits<{
  (e: 'complete'): void;
}>();

const { t } = useI18n();
const { uploadAndCreateWorkshopAppointmentDocument, isPending: isUploadingDocument } =
  useUploadAndCreateWorkshopAppointmentDocument();

const { handleSubmit } = useForm({
  validationSchema: toTypedSchema(
    yup.object({
      name: yup.string().required(),
      type: yup.string().oneOf(Object.values(WorkshopAppointmentDocumentType)).required(),
      file: yup.mixed().required(),
    }),
  ),
});

const saveDocument = handleSubmit(async (values) => {
  await uploadAndCreateWorkshopAppointmentDocument(
    props.workshopAppointmentId,
    values.file as File,
    values.type,
    values.name,
  );
  emit('complete');
});

const documentTypes = computed(() =>
  Object.values(WorkshopAppointmentDocumentType).map((type) => ({
    label: t(`workshopAppointmentDocumentTypes.${type}`),
    value: type,
  })),
);
</script>

<i18n lang="json">
{
  "en": {
    "name": "Name",
    "documentType": "Document Type",
    "upload": "Upload",
    "saveDocument": "Save Document",
    "workshopAppointmentDocumentTypes": {
      "INVOICE": "Invoice",
      "OTHER": "Other"
    }
  },
  "de": {
    "name": "Name",
    "documentType": "Dokumententyp",
    "upload": "Hochladen",
    "saveDocument": "Dokument speichern",
    "workshopAppointmentDocumentTypes": {
      "INVOICE": "Rechnung",
      "OTHER": "Sonstiges"
    }
  }
}
</i18n>
